// data-fit LLC · DecisionFlow · ADMIN Stage
// NotificationCenter.jsx · DDL: R5 · API: v1.11 · Session 94
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: mark-read body {}; local update only; is_read filter sent as string
// Open Items: OI-ADMIN-NOTIFICATION-LIST, OI-ADMIN-NOTIFICATION-READ
// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]
// S285 Track 3: EVENT_CODE_MAP updated with all 6 wired event codes (dot-notation).
//   All 5 prior keys (IMPORT_COMPLETED etc.) were uppercase-underscore and never matched
//   the actual ref_notification_event_type event codes which use dot-notation.
//   Old keys retained for backward compatibility with any legacy rows.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { SCREEN_ORDER } from './AdminFlowShell'
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, X } from 'lucide-react'

const READ_FILTER = { ALL: '', READ: 'true', UNREAD: 'false' }
const EVENT_CODE_FALLBACK = { label: 'General Notification', icon: '🔔' }
const EVENT_CODE_MAP = {
  // S285 Track 3 — wired event codes (dot-notation, matches ref_notification_event_type)
  'dispute.raised':               { label: 'Dispute Raised',               icon: '⚠️'  },
  'dispute.resolved':             { label: 'Dispute Resolved',             icon: '✅'  },
  'amendment.committed':          { label: 'Amendment Committed',          icon: '📝'  },
  'statement_batch.released':     { label: 'Statements Released',          icon: '📄'  },
  'calculation_run.completed':    { label: 'Calculation Completed',        icon: '✅'  },
  'calculation_run.failed':       { label: 'Calculation Failed',           icon: '❌'  },
  // Legacy keys — retained for backward compatibility with pre-S285 rows
  IMPORT_COMPLETED:  { label: 'Import Completed',  icon: '📥' },
  IMPORT_FAILED:     { label: 'Import Failed',      icon: '⚠️' },
  PERIOD_LOCKED:     { label: 'Period Locked',       icon: '🔒' },
  PLAN_UPDATED:      { label: 'Plan Updated',        icon: '🧾' },
  APPROVAL_REQUIRED: { label: 'Approval Required',  icon: '✅' },
}
const NOTIFICATION_NOT_FOUND_MESSAGE = 'Notification not found or no longer available.'

const getErrorEnvelope = (err) => {
  if (err?.error) { const { code, message, http_status } = err.error; return { code, message, http_status } }
  return { code: 'UNKNOWN_ERROR', message: 'An unexpected error occurred.', http_status: null }
}

const parseApiResponse = async (response) => {
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw payload?.error ? payload : { error: { code: 'HTTP_ERROR', http_status: response.status, message: 'Request failed.' } }
  }
  return payload
}

const getRowsFromEnvelope = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

const getPaginationFromEnvelope = (payload) => ({
  next_cursor: payload?.pagination?.next_cursor || null,
  has_more: Boolean(payload?.pagination?.has_more),
})

const getEventCodeMeta = (eventCode) => EVENT_CODE_MAP[eventCode] || EVENT_CODE_FALLBACK
const getBodyPreview = (body) => { if (!body) return '—'; return body.length > 160 ? `${body.slice(0, 160)}…` : body }

export default function NotificationCenter({ aiStatus, aiResponse, currentScreen, onNavigate }) {
  const { token } = useAuth()

  const [notifications, setNotifications] = useState([])
  const [filterIsRead, setFilterIsRead] = useState(READ_FILTER.ALL)
  const [nextCursor, setNextCursor] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isMarkingReadId, setIsMarkingReadId] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [markReadError, setMarkReadError] = useState(null)

  const unreadCount = useMemo(() => notifications.filter((n) => n.is_read === false).length, [notifications])

  const buildQueryParams = ({ cursor = null, activeFilter = filterIsRead } = {}) => {
    const queryParams = new URLSearchParams()
    if (activeFilter !== READ_FILTER.ALL) queryParams.set('is_read', activeFilter)
    if (cursor) queryParams.set('cursor', cursor)
    return queryParams
  }

  const loadNotifications = useCallback(async ({ cursor = null, append = false, activeFilter = filterIsRead } = {}) => {
    if (append) setIsLoadingMore(true); else setIsLoading(true)
    setErrorMessage(null)
    try {
      const queryParams = buildQueryParams({ cursor, activeFilter })
      const queryString = queryParams.toString()
      const url = queryString ? `/api/v1/notifications?${queryString}` : '/api/v1/notifications'
      const response = await fetch(url, { method: 'GET', credentials: 'include' })
      const payload = await parseApiResponse(response)
      const rows = getRowsFromEnvelope(payload)
      const pagination = getPaginationFromEnvelope(payload)
      if (append) setNotifications((current) => [...current, ...rows]); else setNotifications(rows)
      setNextCursor(pagination.next_cursor)
      setHasMore(pagination.has_more)
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      setErrorMessage(message || `Unable to load notifications. (${code})`)
    } finally {
      setIsLoading(false); setIsLoadingMore(false)
    }
  }, [token])

  useEffect(() => { loadNotifications({ activeFilter: READ_FILTER.ALL }) }, [loadNotifications])

  const handleApplyFilter = () => {
    setNotifications([]); setNextCursor(null); setHasMore(false)
    setErrorMessage(null); setMarkReadError(null)
    loadNotifications({ append: false, activeFilter: filterIsRead })
  }

  const handleClearFilter = () => {
    setFilterIsRead(READ_FILTER.ALL); setNotifications([]); setNextCursor(null); setHasMore(false)
    setErrorMessage(null); setMarkReadError(null)
    loadNotifications({ append: false, activeFilter: READ_FILTER.ALL })
  }

  const handleMarkRead = async (notificationId) => {
    setIsMarkingReadId(notificationId); setMarkReadError(null)
    try {
      const response = await fetch(`/api/v1/notifications/${notificationId}/read`, { method: 'PATCH', credentials: 'include', body: JSON.stringify({}) })
      await parseApiResponse(response)
      const now = new Date().toISOString()
      setNotifications((current) => current.map((n) => n.notification_id === notificationId ? { ...n, is_read: true, read_at: now } : n))
    } catch (err) {
      const { code, message, http_status } = getErrorEnvelope(err)
      if (http_status === 404) setMarkReadError(NOTIFICATION_NOT_FOUND_MESSAGE)
      else setMarkReadError(message || `Unable to mark notification as read. (${code})`)
    } finally {
      setIsMarkingReadId(null)
    }
  }

  // renderRows replaced by inline sort/search/checkbox render + side panel (S579)

  // S579 — OI-UI-TABLE-CONSISTENCY
  const [search, setSearch]       = useState('')
  const [sortCol, setSortCol]     = useState('created_at')
  const [sortDir, setSortDir]     = useState('desc')
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [selectedNotif, setSelectedNotif] = useState(null)
  const [bulkMarking, setBulkMarking] = useState(false)

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortCol(col); setSortDir('asc') }
  }

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-text-muted))]" />
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return notifications.filter((n) =>
      !q ||
      (n.title ?? '').toLowerCase().includes(q) ||
      (n.event_code ?? '').toLowerCase().includes(q) ||
      (n.channel ?? '').toLowerCase().includes(q) ||
      (n.body ?? '').toLowerCase().includes(q)
    )
  }, [notifications, search])

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av = '', bv = ''
      if (sortCol === 'event_code')      { av = a.event_code ?? '';  bv = b.event_code ?? '' }
      else if (sortCol === 'title')      { av = a.title ?? '';       bv = b.title ?? '' }
      else if (sortCol === 'channel')    { av = a.channel ?? '';     bv = b.channel ?? '' }
      else if (sortCol === 'is_read')    { av = a.is_read ? '1' : '0'; bv = b.is_read ? '1' : '0' }
      else if (sortCol === 'sent_at')    { av = a.sent_at ?? '';     bv = b.sent_at ?? '' }
      else if (sortCol === 'created_at') { av = a.created_at ?? '';  bv = b.created_at ?? '' }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [filtered, sortCol, sortDir])

  // Bulk mark read
  const selectedUnread = useMemo(() =>
    [...selectedIds].filter((id) => {
      const n = notifications.find((n) => n.notification_id === id)
      return n && !n.is_read
    }), [selectedIds, notifications])

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const handleToggleSelectAll = () => {
    if (selectedIds.size === sorted.length) setSelectedIds(new Set())
    else setSelectedIds(new Set(sorted.map((n) => n.notification_id)))
  }

  const handleBulkMarkRead = async () => {
    if (selectedUnread.length === 0) return
    setBulkMarking(true)
    await Promise.all(selectedUnread.map((id) => handleMarkRead(id)))
    setSelectedIds(new Set())
    setBulkMarking(false)
  }

  const thCls = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] cursor-pointer select-none whitespace-nowrap hover:text-[rgb(var(--color-text))]'
  const tdCls = 'px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap'

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">Notification Center</h2>
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-0.5">Review in-app and email notifications, and mark unread items as read.</p>
        </div>
        <div className="flex items-center gap-1.5 rounded-full bg-[rgb(var(--color-surface-muted))] px-3 py-1 shrink-0">
          <strong className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{unreadCount}</strong>
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Unread</span>
        </div>
      </div>

      {/* Filter — unchanged */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Filters</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]" htmlFor="notification-read-filter">
            Read Status
            <select id="notification-read-filter" value={filterIsRead} onChange={(e) => setFilterIsRead(e.target.value)} className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-1.5 text-tok-sm">
              <option value={READ_FILTER.ALL}>All</option>
              <option value={READ_FILTER.UNREAD}>Unread</option>
              <option value={READ_FILTER.READ}>Read</option>
            </select>
          </label>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button type="button" className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50" onClick={handleApplyFilter} disabled={isLoading}>Apply Filter</button>
          <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50" onClick={handleClearFilter} disabled={isLoading}>Clear Filter</button>
        </div>
      </div>

      {errorMessage && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{errorMessage}</div>}
      {markReadError && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{markReadError}</div>}

      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Notifications {notifications.length > 0 && <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">({notifications.length})</span>}</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {selectedUnread.length > 0 && (
              <button type="button" onClick={handleBulkMarkRead} disabled={bulkMarking}
                className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">
                {bulkMarking ? 'Marking…' : `Mark ${selectedUnread.length} Read`}
              </button>
            )}
            <div className="relative max-w-xs flex-1 min-w-[160px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
              <input
                type="text"
                placeholder="Search notifications…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
              />
            </div>
            {search && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{sorted.length} of {notifications.length}</span>}
          </div>
        </div>

        <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
          <table className="w-full text-tok-sm">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input type="checkbox"
                    checked={sorted.length > 0 && selectedIds.size === sorted.length}
                    onChange={handleToggleSelectAll}
                    className="rounded"
                  />
                </th>
                {[
                  ['event_code','Event'],
                  ['title','Title'],
                  ['channel','Channel'],
                  ['is_read','Status'],
                  ['sent_at','Sent At'],
                  ['created_at','Created At'],
                ].map(([col, label]) => (
                  <th key={col} className={thCls} onClick={() => toggleSort(col)}>
                    {label}<SortIcon col={col} />
                  </th>
                ))}
                <th className="px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan="8" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">Loading notifications…</td></tr>}
              {!isLoading && sorted.length === 0 && (
                <tr><td colSpan="8" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">{search ? 'No notifications match your search.' : 'No notifications found.'}</td></tr>
              )}
              {!isLoading && sorted.map((notification) => {
                const eventMeta = getEventCodeMeta(notification.event_code)
                const isRead = notification.is_read === true
                const isMarkingThisRow = isMarkingReadId === notification.notification_id
                const isSelected = selectedIds.has(notification.notification_id)
                return (
                  <tr
                    key={notification.notification_id}
                    className={`border-t border-[rgb(var(--color-border))] cursor-pointer transition-colors hover:bg-[rgb(var(--color-surface-muted))] ${isSelected ? 'bg-[rgb(var(--color-primary))]/5' : ''} ${!isRead ? 'font-medium' : ''}`}
                    onClick={() => setSelectedNotif(selectedNotif?.notification_id === notification.notification_id ? null : notification)}
                  >
                    <td className="px-3 py-2.5 w-8" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={isSelected} onChange={() => handleToggleSelect(notification.notification_id)} className="rounded" />
                    </td>
                    <td className={tdCls} title={notification.event_code}><span aria-hidden="true">{eventMeta.icon}</span> {eventMeta.label}</td>
                    <td className={tdCls}>{notification.title}</td>
                    <td className={tdCls}>{notification.channel}</td>
                    <td className={tdCls}>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-medium ${isRead ? 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]' : 'bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]'}`}>
                        {isRead ? 'Read' : 'Unread'}
                      </span>
                    </td>
                    <td className={tdCls}>{notification.sent_at || '—'}</td>
                    <td className={tdCls}>{notification.created_at}</td>
                    <td className={tdCls} onClick={(e) => e.stopPropagation()}>
                      {!isRead && (
                        <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50" onClick={() => handleMarkRead(notification.notification_id)} disabled={isMarkingThisRow}>
                          {isMarkingThisRow ? 'Marking…' : 'Mark Read'}
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 self-start" onClick={() => loadNotifications({ cursor: nextCursor, append: true, activeFilter: filterIsRead })} disabled={isLoadingMore}>
            {isLoadingMore ? 'Loading more…' : 'Load More'}
          </button>
        )}
      </div>

      {/* Detail side panel */}
      {selectedNotif && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setSelectedNotif(null)} />
          <div className="fixed top-0 right-0 z-40 h-full w-96 bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col">
            <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <div>
                <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Notification Detail</p>
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))] truncate max-w-[280px]">{selectedNotif.title}</p>
              </div>
              <button onClick={() => setSelectedNotif(null)} className="rounded-lg p-1 hover:bg-[rgb(var(--color-surface-muted))]"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {[
                ['Event', `${getEventCodeMeta(selectedNotif.event_code).icon} ${getEventCodeMeta(selectedNotif.event_code).label}`],
                ['Channel', selectedNotif.channel],
                ['Status', selectedNotif.is_read ? 'Read' : 'Unread'],
                ['Read At', selectedNotif.read_at || '—'],
                ['Sent At', selectedNotif.sent_at || '—'],
                ['Created At', selectedNotif.created_at],
              ].map(([label, val]) => (
                <div key={label}>
                  <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</p>
                  <p className="text-tok-sm text-[rgb(var(--color-text))]">{val}</p>
                </div>
              ))}
              {selectedNotif.body && (
                <div>
                  <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1">Body</p>
                  <p className="text-tok-sm text-[rgb(var(--color-text))] whitespace-pre-wrap">{selectedNotif.body}</p>
                </div>
              )}
              {!selectedNotif.is_read && (
                <div className="pt-2">
                  <button type="button"
                    className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
                    disabled={isMarkingReadId === selectedNotif.notification_id}
                    onClick={() => { handleMarkRead(selectedNotif.notification_id); setSelectedNotif((n) => ({ ...n, is_read: true })) }}>
                    {isMarkingReadId === selectedNotif.notification_id ? 'Marking…' : 'Mark as Read'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
